import { useCallback } from 'react';
import { threadLeadOf } from './threadLead';
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useCatalogItems } from './catalogItems';
import type { Vec3 } from './assemblyPose';
import { smoothstep } from './explodePieces';
import { sizeForCategory } from './formatUnits';
import { dimsUnlessBuilt, mmScale, type BuiltBox, type Dims } from './meshAnnotations';
import { builtBoxOf } from './openaiBlenderApi';
import { partCatalogId, partDbSize } from './partIdentity';
import { usePartModelJobs } from './partModels';
import { getParts, type PartItem } from './partsRun';
import type { StepHole } from './stepHoles';
import { isTrueScale, meshSrc, meshStamp, type Job3D } from './threeDApi';

// THE HARDWARE IN ITS HOLE, shown rather than argued: the part a hole is for, slid into it
// once. No verdict and no numbers — whether it sticks out, is fatter than the bore or goes in
// the wrong face is read off the picture.
//
// THE LENGTH INSIDE THE WOOD IS A GHOST, drawn over the board, and the board stays as it is.
// The board went see-through at first, and a dark walnut at 30% over a white page is beige:
// it read as the part having lost its material. So the hardware is drawn twice instead — solid,
// where nothing is in front of it, and faint through everything, which is the part the wood
// hides.
//
// Its own module because both halves of it are: the table asks which mesh a hole's hardware is
// (useHoleFit), and the viewer places and moves it (placeFit, slide).

// What the viewer needs to draw the hardware of one hole: its curated mesh, and what that mesh
// is scaled into millimetres by — the same three facts a part in the viewer is scaled by (see
// mountAssembly, where each part goes through mmScale on the way in).
export type HoleFit = {
  src: string; dims: Dims | null; trueScale: boolean; built: BuiltBox;
  // The kept model itself — what says whether it was cut from a set (see acrossOf).
  job: Job3D;
  // BUILT CONTACT FACE DOWN — a cover whose build was told which face touches what it covers (see
  // openai_blender._COVER_RULE). Its way in is then known, not guessed off its shape.
  down: boolean;
};

// Whether a kept model was built as a cover: told so on its own run, or cut from a set that was.
export const builtDown = (job: Job3D) => job.params?.cover === true
  || (Array.isArray(job.params?.interactions) && (job.params.interactions as { cover?: boolean }[]).some((one) => one?.cover));

// How long the slide takes. Once, and it then stays where it landed.
export const FIT_MS = 1000;

// WHICH MESH A HOLE'S HARDWARE IS, or null where the Parts DB holds none for it. The curated
// model and nothing else: a run nobody kept is not what the part IS, and a primitive standing
// in for a missing one would hide exactly what the picture is for.
export function useHoleFit(docId: string | null | undefined) {
  const fitOfPart = usePartFit();
  return useCallback((hole: StepHole): HoleFit | null => (
    hole.via == null ? null : fitOfPart(getParts(docId ?? '')[Number(hole.via)])
  ), [docId, fitOfPart]);
}

// THE SAME, OF A PART RATHER THAN A HOLE.
function usePartFit() {
  const { items: catalog, loaded } = useCatalogItems();
  const { modelOf } = usePartModelJobs();
  return useCallback((via: PartItem | undefined): HoleFit | null => {
    if (!via) return null;
    const itemId = partCatalogId(catalog, loaded, via);
    const job = itemId == null ? null : modelOf.get(itemId);
    if (!job) return null;
    return {
      src: meshSrc(job.id, meshStamp(job)),
      // The size its mesh is measured against, by the rule the part viewer uses (see
      // ThreeDStep's row): this manual's reading, else the database's, as its category has it —
      // and only for a model with no built size of its own (see dimsUnlessBuilt).
      dims: dimsUnlessBuilt(builtBoxOf(job), sizeForCategory(partDbSize(catalog, itemId, via.size), via.category ?? '')),
      trueScale: isTrueScale(job),
      built: builtBoxOf(job),
      down: builtDown(job),
      job,
    };
  }, [catalog, loaded, modelOf]);
}

type Three = typeof import('three');
type Obj = import('three').Object3D;

// Loaded once per mesh and cloned per use, for the whole page: a rail with four barrel nuts is
// one download, and a part every sub-step of Phase 6 stands is read once (see loadCopy).
const loads = new Map<string, Promise<Obj | null>>();

export function loadFit(loader: GLTFLoader, src: string): Promise<Obj | null> {
  let got = loads.get(src);
  if (!got) {
    got = new Promise((resolve) => {
      loader.load(src, (gltf) => resolve(gltf.scene), undefined, () => resolve(null));
    });
    loads.set(src, got);
  }
  return got;
}

// A COPY OF ITS OWN, out of that same once-per-page load: the geometry shared, the materials
// not. The draw modes and hiding a mesh's row set things on its material (see meshDrawModes),
// and a piece stands the same part's file more than once.
export function loadCopy(loader: GLTFLoader, src: string): Promise<Obj | null> {
  return loadFit(loader, src).then((source) => {
    if (!source) return null;
    const copy = source.clone(true);
    copy.traverse((o) => {
      const mesh = o as import('three').Mesh;
      if (!mesh.isMesh) return;
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map((m) => m.clone()) : mesh.material.clone();
    });
    return copy;
  });
}

// One piece of hardware on its way in: where it starts, where it stops, and when it set off.
export type Sliding = {
  obj: Obj;
  from: import('three').Vector3;
  to: import('three').Vector3;
  t0: number;
};

// THE HARDWARE, SCALED, TURNED ONTO THE BORE AND STANDING OUTSIDE ITS MOUTH — or null where
// its size cannot be put in millimetres, which would be a guess at how big it is.
//
// In the frame the hole marks are drawn in: `at` the mouth, `n` the face's outward normal.
// Its LONG axis goes along the bore and its WIDER end out, which is the head of a screw; a
// dowel or a barrel nut is the same either way round. A through hole takes it all the way, its
// outer end flush with the mouth; a blind one until its tip meets the floor.
// WHICH WAY A PIECE OF HARDWARE GOES INTO ITS HOLE, and how long it is: along its AXIS OF SYMMETRY,
// from its wide end (a head) toward its narrow one. `obj` in millimetres, centred on the origin —
// see placeFit, and the joint view, which stands the hardware in its holes as parts. `even` where
// its two ends are alike, so either way round is right and the caller may pick the short turn.
//
// THE AXIS WHOSE TWO OTHER EXTENTS ARE MOST NEARLY EQUAL, and not the longest one. The two agree on
// a screw, a dowel, a barrel nut — each long, and round across — and part on a felt pad, whose
// longest extent is a diameter: it went into its "hole" edge first and stood on its side under the
// foot. Its round way across is its thickness. Where no axis is clearly the round one (a box of a
// thing), the longest, as before.
//
// A COVER BUILT CONTACT FACE DOWN goes in downward, and nothing is guessed: Blender's -Z, which is
// the file's -Z too — a written build is exported Z-up (export_yup=False), not glTF's Y-up. A wood button is a dome over a plug, and "wide end out" read the dome's narrow top
// as the plug — every button went in upside down.
const ROUND = 0.12;
// `reach`: how far any of it stands from its axis — a cover's half-width, what it touches with.
export function fitAxis(THREE: Three, obj: Obj, down = false): { into: Vec3; len: number; even: boolean; reach: number } {
  obj.updateMatrixWorld(true);
  const size = new THREE.Box3().setFromObject(obj).getSize(new THREE.Vector3());
  if (down) return { into: [0, 0, -1], len: size.z, even: false, reach: Math.max(size.x, size.y) / 2 };
  const mm = [size.x, size.y, size.z];
  const unround = [0, 1, 2].map((a) => {
    const [p, q] = [0, 1, 2].filter((c) => c !== a).map((c) => mm[c]);
    return Math.abs(p - q) / Math.max(p, q, 1e-9);
  });
  const roundest = unround.indexOf(Math.min(...unround));
  const clear = unround.filter((_, a) => a !== roundest).every((u) => u - unround[roundest] > ROUND);
  const ax = clear ? roundest : mm.indexOf(Math.max(...mm));
  const len = mm[ax];
  // WHICH END IS WIDER: the furthest any vertex sits from the axis, near each end.
  const reach = [0, 0];
  let widest = 0;
  const p = new THREE.Vector3();
  obj.traverse((child) => {
    const m = child as import('three').Mesh;
    const pos = m.isMesh ? m.geometry?.getAttribute('position') : null;
    if (!pos) return;
    for (let i = 0; i < pos.count; i += 1) {
      p.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld);
      const t = p.getComponent(ax);
      const r = Math.hypot(...[0, 1, 2].filter((c) => c !== ax).map((c) => p.getComponent(c)));
      if (r > widest) widest = r;
      if (Math.abs(t) < len * 0.35) continue;
      const end = t > 0 ? 0 : 1;
      if (r > reach[end]) reach[end] = r;
    }
  });
  // Into the hole is from the wide end towards the narrow one.
  const into: [number, number, number] = [0, 0, 0];
  into[ax] = reach[0] >= reach[1] ? -1 : 1;
  const even = Math.abs(reach[0] - reach[1]) <= 0.05 * Math.max(reach[0], reach[1], 1e-9);
  return { into, len, even, reach: widest };
}

// THE SAME, OF A FIT'S OWN MESH: loaded, put in millimetres by the fit's rule and centred — and
// how far it advances a turn where its model carries a thread (see threadLeadOf; null where not).
export type FitAxis = { into: Vec3; len: number; even: boolean; reach: number; lead: number | null };
export async function fitAxisOf(
  THREE: Three, loader: GLTFLoader, fit: HoleFit,
): Promise<FitAxis | null> {
  const holder = await fitHolder(THREE, loader, fit);
  if (!holder) return null;
  const axis = fitAxis(THREE, holder, fit.down);
  return { ...axis, lead: await threadLeadOf(THREE, holder, axis).catch(() => null) };
}

// A FIT'S OWN MESH in millimetres, centred — the frame fitAxisOf reads its axis in.
async function fitHolder(THREE: Three, loader: GLTFLoader, fit: HoleFit): Promise<Obj | null> {
  const source = await loadFit(loader, fit.src);
  if (!source) return null;
  const obj = source.clone(true);
  const box = new THREE.Box3().setFromObject(obj);
  const raw = box.getSize(new THREE.Vector3());
  const k = mmScale({ x: raw.x, y: raw.y, z: raw.z }, fit.dims ?? { x: null, y: null, z: null },
                    fit.trueScale, fit.built);
  if (!k) return null;
  obj.scale.setScalar(k);
  obj.position.copy(box.getCenter(new THREE.Vector3())).multiplyScalar(-k);
  const holder = new THREE.Group();
  holder.add(obj);
  holder.updateMatrixWorld(true);
  return holder;
}

// THE BORE ACROSS A PIECE — a barrel nut's threaded hole, square to its own axis — read off its mesh,
// in the frame fitAxisOf reads the axis in. Null where it has none.
//
// LINES ACROSS THE AXIS, through it, every BORE_STEP_DEG round and at a few places along its middle:
// along the bore a line meets nothing (it runs down the hole and out of both mouths); turned off it,
// the bore's own wall. So the clearest way across is the bore, and where several angles are clear
// (a line a few degrees off still leaves by the mouth) the middle of them. A piece clear the same
// every way round — a solid rod, a tube — has no bore to turn. Only the middle of its length is
// searched: a screwdriver slot cut across one END is a clear way across too.
const BORE_STEP_DEG = 5;
const BORE_ALONG = [-0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3];
export async function crossBoreOf(THREE: Three, loader: GLTFLoader, fit: HoleFit, into: Vec3): Promise<Vec3 | null> {
  const holder = await fitHolder(THREE, loader, fit);
  if (!holder) return null;
  const ax = into.findIndex((c) => c !== 0);
  const [u, v] = [0, 1, 2].filter((c) => c !== ax);
  // Both sides of every face: a line starts outside and has to see the faces it leaves by as well.
  const both = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
  holder.traverse((child) => { const m = child as import('three').Mesh; if (m.isMesh) m.material = both; });
  const size = new THREE.Box3().setFromObject(holder).getSize(new THREE.Vector3());
  const len = size.getComponent(ax);
  const far = Math.max(size.x, size.y, size.z) * 2;
  const ray = new THREE.Raycaster();
  const angles = Array.from({ length: 180 / BORE_STEP_DEG }, (_, i) => i * BORE_STEP_DEG);
  // How far a line across stays clear of the mesh either side of the axis, at its clearest place along it.
  const clear = angles.map((deg) => {
    const d = new THREE.Vector3();
    d.setComponent(u, Math.cos((deg * Math.PI) / 180));
    d.setComponent(v, Math.sin((deg * Math.PI) / 180));
    return Math.max(...BORE_ALONG.map((t) => {
      const p = new THREE.Vector3();
      p.setComponent(ax, t * len);
      ray.set(p.clone().addScaledVector(d, -far), d);
      ray.far = 2 * far;
      const hits = ray.intersectObject(holder, true).map((h) => Math.abs(h.distance - far));
      return hits.length ? Math.min(...hits) : far;
    }));
  });
  const most = Math.max(...clear);
  // NOTHING STANDS OUT: as clear one way as every other.
  if (!(Math.min(...clear) < most * 0.6)) return null;
  // The middle of the clearest run, round the half turn (0° and 175° are neighbours): averaged as
  // doubled angles, which makes a direction and its opposite the same.
  const top = angles.filter((_, i) => clear[i] >= most * 0.99);
  const [c, s] = top.reduce(([x, y], deg) => [x + Math.cos((deg * Math.PI) / 90), y + Math.sin((deg * Math.PI) / 90)], [0, 0]);
  const mid = Math.atan2(s, c) / 2;
  const out = [0, 0, 0];
  out[u] = Math.cos(mid);
  out[v] = Math.sin(mid);
  return [out[0], out[1], out[2]];
}

export function placeFit(
  THREE: Three,
  source: Obj,
  fit: HoleFit,
  at: Vec3,
  n: Vec3,
  bore: number,
  through: boolean,
): Sliding | null {
  const obj = source.clone(true);
  const box = new THREE.Box3().setFromObject(obj);
  const raw = box.getSize(new THREE.Vector3());
  const k = mmScale({ x: raw.x, y: raw.y, z: raw.z }, fit.dims ?? { x: null, y: null, z: null },
                    fit.trueScale, fit.built);
  if (!k) return null;
  obj.scale.setScalar(k);
  obj.position.copy(box.getCenter(new THREE.Vector3())).multiplyScalar(-k);
  const pivot = new THREE.Group();
  pivot.add(obj);
  // AND ITS GHOST, the same shape drawn faint and through the board: what the wood hides.
  // Materials of its own, since the solid copy shares the loaded mesh's.
  const ghost = obj.clone(true);
  ghost.traverse((child) => {
    const m = child as import('three').Mesh;
    if (!m.isMesh) return;
    const faint = (mat: import('three').Material) => {
      const c = mat.clone();
      c.transparent = true;
      c.opacity = 0.35;
      c.depthTest = false;
      c.depthWrite = false;
      return c;
    };
    m.material = Array.isArray(m.material) ? m.material.map(faint) : faint(m.material);
    m.renderOrder = 10;
  });
  pivot.add(ghost);
  pivot.updateMatrixWorld(true);

  const { into: along, len } = fitAxis(THREE, pivot, fit.down);
  const into = new THREE.Vector3(...along);
  const inward = new THREE.Vector3(-n[0], -n[1], -n[2]);
  pivot.quaternion.setFromUnitVectors(into, inward);

  const inserted = through || !(bore > 0) ? len : Math.min(len, bore);
  const mouth = new THREE.Vector3(at[0], at[1], at[2]);
  // The centre, `down` millimetres into the part along the bore.
  const centreAt = (down: number) => mouth.clone().addScaledVector(inward, down);
  const to = centreAt(inserted - len / 2);
  const from = centreAt(-len / 2 - Math.max(len * 0.5, 5));
  pivot.position.copy(from);
  return { obj: pivot, from, to, t0: performance.now() };
}

// One frame of the slide. Eased at both ends, so it leaves and arrives at rest.
export function slide(s: Sliding, now: number): void {
  const k = Math.min(1, (now - s.t0) / FIT_MS);
  s.obj.position.lerpVectors(s.from, s.to, smoothstep(k));
}
